/* eslint-disable no-restricted-globals */
/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { mapValues } from 'lodash';
import './css/AdminPanel.css';
import {
  SAVE,
  FLIGHT_INSURANCE,
} from '../../Utils/Constants/GlobalConstants';
import {
  SPLIT,
  OPERATOR_SETTINGS,
  DEPARTURE_SETTINGS,
  REGULAR_SPLIT,
  CHARTER_SPLIT,
  INSURANCE_AVAILABLE,
  BEFORE,
  DAYS,
  PERCENT,
} from '../../Utils/Constants/AdminConstants';
import { CITY_OPTIONS } from '../../Utils/Constants/CitiesFrom';
import { ReactComponent as DefaultCheck } from '../../images/default-check-r.svg';
import { ReactComponent as ActiveCheck } from '../../images/active-check-r.svg';

const splitTypes = [
  { type: 'regularSplit', title: REGULAR_SPLIT },
  { type: 'charterSplit', title: CHARTER_SPLIT },
];

const SPLIT_FIELDS_COUNT = 3;

// Missing split values are shown as 0
const getSplitFields = (split) =>
  Array.from({ length: SPLIT_FIELDS_COUNT }, (_, i) => split[i] || 0);

// Zero split values are dropped before saving
const getOperatorToSave = (operator) => ({
  ...operator,
  departures: mapValues(operator.departures, (departure) => ({
    ...departure,
    countries: mapValues(departure.countries, (country) => ({
      ...country,
      split: mapValues(country.split, (item) => ({
        ...item,
        split: item.split.filter((percent) => percent > 0),
      })),
    })),
  })),
});

function Operator(props) {
  const { operator, onChange, onSave } = props;
  const departures = Object.values(operator.departures || {});
  // Minsk by default
  const [departureId, setDepartureId] = useState(CITY_OPTIONS[0].id);

  const departure =
    (operator.departures && operator.departures[departureId]) ||
    departures[0];

  const updateDeparture = (newDeparture) =>
    onChange({
      ...operator,
      departures: {
        ...operator.departures,
        [departure.departureId]: newDeparture,
      },
    });

  const updateCountry = (countryId, getNewCountry) =>
    updateDeparture({
      ...departure,
      countries: {
        ...departure.countries,
        [countryId]: getNewCountry(departure.countries[countryId]),
      },
    });

  const handleDepartureFlagChange = (field) =>
    updateDeparture({ ...departure, [field]: !departure[field] });

  const updateSplit = (countryId, splitType, getNewSplit) =>
    updateCountry(countryId, (country) => ({
      ...country,
      split: {
        ...country.split,
        [splitType]: getNewSplit(country.split[splitType]),
      },
    }));

  const handleInsuranceChange = (countryId, value) =>
    updateCountry(countryId, (country) => ({
      ...country,
      insurance: { ...country.insurance, beforeDays: Number(value) },
    }));

  const handleInsuranceEnabledChange = (countryId) =>
    updateCountry(countryId, (country) => ({
      ...country,
      insurance: {
        ...country.insurance,
        insuranceEnabled: !(
          country.insurance && country.insurance.insuranceEnabled
        ),
      },
    }));

  const handleSplitBeforeDaysChange = (countryId, splitType, value) =>
    updateSplit(countryId, splitType, (split) => ({
      ...split,
      beforeDays: Number(value),
    }));

  const handleSplitPercentChange = (countryId, splitType, index, value) =>
    updateSplit(countryId, splitType, (split) => {
      const newSplit = getSplitFields(split.split);
      newSplit[index] = Number(value);

      return { ...split, split: newSplit };
    });

  const handleSplitEnabledChange = (countryId, splitType) =>
    updateSplit(countryId, splitType, (split) => ({
      ...split,
      splitEnabled: !split.splitEnabled,
    }));

  const renderNumberInput = (value, onValueChange, maxLength) => (
    <input
      className="ap_input"
      type="text"
      value={value}
      onChange={(e) => {
        if (!isNaN(e.target.value)) {
          onValueChange(e.target.value);
        }
      }}
      maxLength={maxLength}
    />
  );

  const renderCheck = (checked, onClick, title, className) => (
    <button
      type="button"
      className={`ap_check ${className}`}
      onClick={onClick}
    >
      {checked ? (
        <ActiveCheck className="ap_check_ico" />
      ) : (
        <DefaultCheck className="ap_check_ico" />
      )}
      <div>{title}</div>
    </button>
  );

  const getDepartureClass = (id) =>
    `ap_small_btn ${
      departure.departureId === id
        ? 'btn__blue wm_text_14'
        : 'light_gray_button bm_text_14'
    }`;

  const renderDepartures = () =>
    departures.map((item) => (
      <button
        key={`ap-departure-${item.departureId}`}
        type="button"
        className={getDepartureClass(item.departureId)}
        onClick={() => setDepartureId(item.departureId)}
      >
        {item.departureName}
      </button>
    ));

  const renderSplit = (country, splitType) => {
    const { countryId } = country;
    const split = country.split && country.split[splitType.type];

    if (!split) {
      return null;
    }

    return (
      <div key={`ap-split-${splitType.type}`} className="ap_country_split">
        {renderCheck(
          split.splitEnabled,
          () => handleSplitEnabledChange(countryId, splitType.type),
          splitType.title,
          'br_text_14',
        )}
        {split.splitEnabled && (
          <div>
            <div className="ap_form_line">
              <div className="br_text_14">{BEFORE}</div>
              {renderNumberInput(
                split.beforeDays,
                (value) =>
                  handleSplitBeforeDaysChange(countryId, splitType.type, value),
                3,
              )}
              <div className="br_text_14">{DAYS}</div>
            </div>
            <div className="ap_form_line">
              <div className="br_text_14">{PERCENT}</div>
              {getSplitFields(split.split).map((percent, index) => (
                <React.Fragment key={`ap-split-percent-${index}`}>
                  {renderNumberInput(
                    percent,
                    (value) =>
                      handleSplitPercentChange(
                        countryId,
                        splitType.type,
                        index,
                        value,
                      ),
                    3,
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInsurance = (country) => {
    const { countryId, insurance = {} } = country;

    return (
      <div className="ap_country_split">
        {renderCheck(
          insurance.insuranceEnabled,
          () => handleInsuranceEnabledChange(countryId),
          INSURANCE_AVAILABLE,
          'br_text_14',
        )}
        {insurance.insuranceEnabled && (
          <div className="ap_form_line">
            <div className="br_text_14">{BEFORE}</div>
            {renderNumberInput(
              insurance.beforeDays,
              (value) => handleInsuranceChange(countryId, value),
              2,
            )}
            <div className="br_text_14">{DAYS}</div>
          </div>
        )}
      </div>
    );
  };

  const isSplitEnabled =
    operator.splitEnabled && departure && departure.splitEnabled;
  const isInsuranceEnabled =
    operator.insuranceEnabled && departure && departure.insuranceEnabled;

  const renderCountry = (country) => (
    <div key={`ap-country-${country.countryId}`} className="ap_country">
      <div className="bm_text_16 mb_10">{country.countryName}</div>
      {isInsuranceEnabled && renderInsurance(country)}

      {isSplitEnabled &&
        splitTypes.map((splitType) => renderSplit(country, splitType))}
    </div>
  );

  return (
    <div className="ap_card ap_operator mb_40">
      <div className="ap_operator_header mb_16">
        <div className="bm_text_22">{operator.name}</div>
        <button
          type="button"
          onClick={() => onSave(getOperatorToSave(operator))}
          className="ap_btn btn__yellow dbm_text_16"
        >
          {SAVE}
        </button>
      </div>
      <div className="gm_text_14 mb_10">{OPERATOR_SETTINGS}</div>
      {renderCheck(
        operator.splitEnabled,
        () => onChange({ ...operator, splitEnabled: !operator.splitEnabled }),
        SPLIT,
        'bm_text_16 mb_10',
      )}
      {renderCheck(
        operator.insuranceEnabled,
        () =>
          onChange({
            ...operator,
            insuranceEnabled: !operator.insuranceEnabled,
          }),
        FLIGHT_INSURANCE,
        'bm_text_16 mb_16',
      )}
      {departure && (
        <div className="ap_departure mb_16">
          <div className="gm_text_14 mb_10">{DEPARTURE_SETTINGS}</div>
          <div className="ap_tabs mb_16">{renderDepartures()}</div>
          {renderCheck(
            departure.splitEnabled,
            () => handleDepartureFlagChange('splitEnabled'),
            SPLIT,
            'bm_text_16 mb_10',
          )}
          {renderCheck(
            departure.insuranceEnabled,
            () => handleDepartureFlagChange('insuranceEnabled'),
            FLIGHT_INSURANCE,
            'bm_text_16 mb_16',
          )}
          {(isSplitEnabled || isInsuranceEnabled) && (
            <div className="ap_countries mb_16">
              {Object.values(departure.countries || {}).map(renderCountry)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

Operator.propTypes = {
  operator: PropTypes.instanceOf(Object).isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default Operator;
